import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

export type TourMode = 'full' | 'first-run';

interface TooltipTourContextType {
  isRunning: boolean;
  tourMode: TourMode;
  startTour: (mode?: TourMode) => void;
  stopTour: () => void;
}

const TooltipTourContext = createContext<TooltipTourContextType | undefined>(undefined);

export function useTooltipTour() {
  const context = useContext(TooltipTourContext);
  if (!context) {
    throw new Error('useTooltipTour must be used within a TooltipTourProvider');
  }
  return context;
}

interface TooltipTourProviderProps {
  children: ReactNode;
}

export const TooltipTourProvider: React.FC<TooltipTourProviderProps> = ({ children }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [tourMode, setTourMode] = useState<TourMode>('full');

  const startTour = useCallback((mode: TourMode = 'full') => {
    setTourMode(mode);
    setIsRunning(true);
  }, []);

  const stopTour = useCallback(() => {
    setIsRunning(false);
    setTourMode('full');
  }, []);

  const value = useMemo(
    () => ({ isRunning, tourMode, startTour, stopTour }),
    [isRunning, tourMode, startTour, stopTour]
  );

  return (
    <TooltipTourContext.Provider value={value}>
      {children}
    </TooltipTourContext.Provider>
  );
};
